import { useState, useEffect, useRef } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { firestoreDb } from "@/lib/firebase";

/**
 * Maps the C# OrderStatus enum:
 * New=0, Rejected=1, Accepted=2, OnProgress=3, Ready=4,
 * OnDelivery=5, Delivered=6, Pending=7, DeliveredToDelivery=8
 */
export type OrderStatusCode = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

export type RejectionReasonCode = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

export const ORDER_STATUS_MAP: Record<OrderStatusCode, string> = {
  0: "new",
  1: "rejected",
  2: "accepted",
  3: "on-progress",
  4: "ready",
  5: "on-delivery",
  6: "delivered",
  7: "pending",
  8: "delivered-to-delivery",
};

// StoreRejectionReason (0–9) — set by the restaurant/store
export const STORE_REJECTION_REASONS_EN: Record<number, string> = {
  0: "No reason provided",
  1: "Some items are out of stock",
  2: "All items are out of stock",
  3: "Kitchen overload / delay",
  4: "Closed – outside working hours",
  5: "Outside delivery zone",
  6: "Address unreachable or restricted",
  7: "Payment failed",
  8: "Customer refused to receive",
  9: "Delivery date not suitable",
};

export const STORE_REJECTION_REASONS_AR: Record<number, string> = {
  0: "لم يُذكر سبب",
  1: "بعض الأصناف غير متوفرة",
  2: "جميع الأصناف غير متوفرة",
  3: "ضغط على المطبخ / تأخير",
  4: "مغلق – خارج ساعات العمل",
  5: "خارج نطاق التوصيل",
  6: "العنوان غير قابل للوصول أو مقيّد",
  7: "فشل الدفع",
  8: "العميل رفض الاستلام",
  9: "موعد التوصيل غير مناسب",
};

// Backward-compat aliases
export const REJECTION_REASONS_EN = STORE_REJECTION_REASONS_EN;
export const REJECTION_REASONS_AR = STORE_REJECTION_REASONS_AR;

// OrderRejectionReason (0–6) — set by delivery/system
export const ORDER_REJECTION_REASONS_EN: Record<number, string> = {
  0: "Restaurant rejected the order",
  1: "Delivery rejected the order",
  2: "Order exceeded waiting time",
  3: "Customer refused to pay",
  4: "Payment attempts exhausted",
  5: "Payment failed",
  6: "Delivery rejected the order",
};

export const ORDER_REJECTION_REASONS_AR: Record<number, string> = {
  0: "المطعم رفض الطلب",
  1: "التوصيل رفض الطلب",
  2: "الطلب تخطى وقت الانتظار",
  3: "العميل رفض الدفع",
  4: "استنفاذ عدد مرات الدفع",
  5: "فشل عملية الدفع",
  6: "التوصيل رفض الطلب",
};

export interface FirebaseOrderItem {
  name: string;
  name_ar?: string;
  quantity: number;
  price: number;
}

export interface FirebaseOrder {
  status: OrderStatusCode;
  rejection_reason?: RejectionReasonCode;
  rejection_note?: string;
  order_number: string;
  items: FirebaseOrderItem[];
  total: number;
  estimated_minutes: number;
  placed_at: string;
  driver_name?: string;
  driver_phone?: string;
  delivery_address?: string;
  order_type?: string;
  currency_symbol?: string;
  // StoreRejectionReason (0–9): show if != 0
  store_rejection_reason?: number;
  // RejectionReason (-1 to 6): show if != -1
  order_rejection_reason?: number;
  out_of_stock_items?:number[] | null;

}

type FirestoreTimestamp = {
  type?: string;
  seconds: number;
  nanoseconds?: number;
};

type NameLangEntry = {
  value: string;
  lang: string;
};

type RawFirebaseOrderItem = {
  name?: string;
  name_ar?: string;
  nameAr?: string;
  quantity?: number | string | null;
  qty?: number | string | null;
  price?: number | string | null;
  unit_price?: number | string | null;
  unitPrice?: number | string | null;
  total_price?: number | string | null;
  totalPrice?: number | string | null;
};

type RawMealItem = {
  productId?: string;
  quantity?: number | string | null;
  price?: number | string | null;
  nameLang?: NameLangEntry[] | null;
  modifierValues?: Array<{
    type: string;
    features: unknown[];
  }> | null;
  offer?: boolean;
  productType?: number;
  bookDetail?: unknown;
};

type RawDeliveryInfo = {
  driverName?: string | null;
  driverPhone?: string | null;
  driver_name?: string | null;
  driver_phone?: string | null;
};

type RawFirebaseOrder = {
  status?: number | string | null;
  rejection_reason?: number | string | null;
  rejectionReason?: number | string | null;
  rejection_note?: string | null;
  rejectionNote?: string | null;
  order_number?: string | null;
  orderNumber?: string | null;
  orderId?: string | null;
  orderGuid?: string | null;
  items?: RawFirebaseOrderItem[] | null;
  orderItems?: RawFirebaseOrderItem[] | null;
  meal?: RawMealItem[] | null;
  total?: number | string | null;
  invoiceValue?: number | string | null;
  invoiceValueBeforeDiscount?: number | string | null;
  orderValue?: number | string | null;
  orderValueWithVat?: number | string | null;
  estimated_minutes?: number | string | null;
  estimatedMinutes?: number | string | null;
  waitingTime?: number | string | null;
  placed_at?: string | FirestoreTimestamp | null;
  placedAt?: string | FirestoreTimestamp | null;
  createdAt?: string | FirestoreTimestamp | null;
  driver_name?: string | null;
  driverName?: string | null;
  driver_phone?: string | null;
  driverPhone?: string | null;
  delivery?: RawDeliveryInfo | null;
  delivery_address?: string | null;
  deliveryAddress?: string | null;
  deliveryAdress?: string | null;
  order_type?: string | null;
  orderType?: string | null;
  deliveryOption?: number | string | null;
  currency_symbol?: string | null;
  currencySymbol?: string | null;
  store_rejection_reason?: number | string | null;
  storeRejectionReason?: number | string | null;
  branchId?: string | null;
  branchName?: string | null;
  projectId?: string | null;
  log?: Array<{ status: number; date: string; userId?: string | null }> | null;
  outOfStockItems?:number[] | null;
};

function splitFirestorePath(path: string) {
  return path.split("/").filter(Boolean);
}

function parseLocalizedNumber(
  value: string | number | null | undefined,
  useCommaDecimal?: boolean
): number {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;

  let str = String(value).trim();
  str = str.replace(/[¤$€£¥\s]/g, "");

  const lastComma = str.lastIndexOf(",");
  const lastDot = str.lastIndexOf(".");
  const isCommaDecimal = useCommaDecimal ?? (lastComma > lastDot);

  if (isCommaDecimal) {
    str = str.replace(/\./g, "");
    str = str.replace(",", ".");
  } else {
    str = str.replace(/,/g, "");
  }

  const parsed = Number.parseFloat(str);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeStatusCode(value: unknown): OrderStatusCode {
  const parsed = Math.trunc(parseLocalizedNumber(value as string | number | null | undefined));
  return Object.prototype.hasOwnProperty.call(ORDER_STATUS_MAP, parsed)
    ? (parsed as OrderStatusCode)
    : 7;
}

function normalizeRejectionReason(value: unknown): RejectionReasonCode | undefined {
  const parsed = Math.trunc(parseLocalizedNumber(value as string | number | null | undefined));
  return parsed >= 0 && parsed <= 9 ? (parsed as RejectionReasonCode) : undefined;
}

function normalizeTimestamp(value: string | FirestoreTimestamp | null | undefined): string {
  if (!value) return new Date().toISOString();

  // Firestore Timestamp object: { seconds: number, nanoseconds?: number }
  if (typeof value === "object" && value !== null && "seconds" in value) {
    const ts = value as FirestoreTimestamp;
    return new Date(ts.seconds * 1000).toISOString();
  }

  // Already a string — validate it's a valid date
  if (typeof value === "string") {
    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) return value;
  }

  return new Date().toISOString();
}

function normalizeOrderItem(item: RawFirebaseOrderItem, index: number): FirebaseOrderItem {
  const quantity = Math.max(
    1,
    Math.trunc(parseLocalizedNumber(item.quantity ?? item.qty ?? 1)) || 1
  );
  const totalPrice = parseLocalizedNumber(item.total_price ?? item.totalPrice);
  const unitPrice = parseLocalizedNumber(item.price ?? item.unit_price ?? item.unitPrice);
  const price = unitPrice || (totalPrice > 0 ? totalPrice / quantity : 0);

  return {
    name: item.name?.trim() || `Item ${index + 1}`,
    name_ar: item.name_ar?.trim() || item.nameAr?.trim() || undefined,
    quantity,
    price,
  };
}

function extractNameFromNameLang(
  nameLang: NameLangEntry[] | null | undefined,
  lang: string
): string | undefined {
  if (!nameLang || !Array.isArray(nameLang)) return undefined;
  const entry = nameLang.find((n) => n.lang === lang);
  return entry?.value?.trim() || undefined;
}

function normalizeMealItem(item: RawMealItem, index: number): FirebaseOrderItem {
  const quantity = Math.max(
    1,
    Math.trunc(parseLocalizedNumber(item.quantity ?? 1)) || 1
  );
  const price = parseLocalizedNumber(item.price);

  const nameEn = extractNameFromNameLang(item.nameLang, "en");
  const nameAr = extractNameFromNameLang(item.nameLang, "ar");

  return {
    name: nameEn || nameAr || `Item ${index + 1}`,
    name_ar: nameAr,
    quantity,
    price,
  };
}

function normalizeFirebaseOrder(data: RawFirebaseOrder, snapshotId: string): FirebaseOrder {
  let items: FirebaseOrderItem[];

  if (Array.isArray(data.meal) && data.meal.length > 0) {
    items = data.meal.map(normalizeMealItem);
  } else if (Array.isArray(data.items) && data.items.length > 0) {
    items = data.items.map(normalizeOrderItem);
  } else if (Array.isArray(data.orderItems) && data.orderItems.length > 0) {
    items = data.orderItems.map(normalizeOrderItem);
  } else {
    items = [];
  }

  const driverName =
    data.driver_name ??
    data.driverName ??
    data.delivery?.driver_name ??
    data.delivery?.driverName ??
    undefined;

  const driverPhone =
    data.driver_phone ??
    data.driverPhone ??
    data.delivery?.driver_phone ??
    data.delivery?.driverPhone ??
    undefined;

  const deliveryAddress =
    data.delivery_address ??
    data.deliveryAddress ??
    data.deliveryAdress ??
    undefined;

  const estimatedMinutes = Math.max(
    0,
    Math.trunc(
      parseLocalizedNumber(
        data.estimated_minutes ?? data.estimatedMinutes ?? data.waitingTime ?? 25
      )
    ) || 25
  );

  const orderNumber =
    data.order_number ??
    data.orderNumber ??
    data.orderId ??
    snapshotId;

  const orderType =
    data.order_type ??
    data.orderType ??
    (data.deliveryOption != null ? String(data.deliveryOption) : undefined);

  return {
    status: normalizeStatusCode(data.status),
    rejection_reason: normalizeRejectionReason(data.rejection_reason ?? data.rejectionReason),
    rejection_note: data.rejection_note ?? data.rejectionNote ?? undefined,
    order_number: orderNumber,
    items,
    total: parseLocalizedNumber(data.total ?? data.invoiceValue ?? data.orderValueWithVat),
    estimated_minutes: estimatedMinutes,
    placed_at: normalizeTimestamp(data.placed_at ?? data.placedAt ?? data.createdAt),
    driver_name: driverName,
    driver_phone: driverPhone,
    delivery_address: deliveryAddress,
    order_type: orderType,
    currency_symbol: data.currency_symbol ?? data.currencySymbol ?? undefined,
    store_rejection_reason:
      data.store_rejection_reason != null || data.storeRejectionReason != null
        ? Math.trunc(parseLocalizedNumber(data.store_rejection_reason ?? data.storeRejectionReason))
        : undefined,
    order_rejection_reason:
      data.rejection_reason != null || data.rejectionReason != null
        ? Math.trunc(parseLocalizedNumber(data.rejection_reason ?? data.rejectionReason))
        : undefined,

      out_of_stock_items: Array.isArray(data.outOfStockItems) ? data.outOfStockItems : undefined,

  };
}

export function useFirebaseOrderStatus(firebasePath: string | undefined) {
  const [order, setOrder] = useState<FirebaseOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const prevStatusRef = useRef<OrderStatusCode | null>(null);
  const [statusChanged, setStatusChanged] = useState(false);

  useEffect(() => {
    if (!firebasePath) {
      console.warn("[Firestore Track] No Firestore path provided to listener.");
      setLoading(false);
      setError("No Firestore path provided");
      return;
    }

    const sanitizedPath = firebasePath.replace(/^\/+|\/+$/g, "");
    const pathSegments = splitFirestorePath(sanitizedPath);

    console.group("[Firestore Track] Subscribe");
    console.log("firestorePath(raw)", firebasePath);
    console.log("firestorePath(sanitized)", sanitizedPath);
    console.log("pathSegments", pathSegments);
    console.log("segmentCount", pathSegments.length);
    console.log("isDocumentPath", pathSegments.length % 2 === 0);
    console.log("firestore listen call", `doc(firestoreDb, \"${sanitizedPath}\")`);
    console.groupEnd();

    if (pathSegments.length % 2 !== 0) {
      const message = `Invalid Firestore document path: ${sanitizedPath}`;
      console.error("[Firestore Track] Invalid document path", {
        path: sanitizedPath,
        pathSegments,
      });
      setOrder(null);
      setError(message);
      setLoading(false);
      return;
    }

    const orderRef = doc(firestoreDb, sanitizedPath);
    const unsubscribe = onSnapshot(
      orderRef,
      (snapshot) => {
        const data = snapshot.data();
        console.group("[Firestore Track] Snapshot");
        console.log("path", sanitizedPath);
        console.log("pathSegments", pathSegments);
        console.log("exists", snapshot.exists());
        console.log("snapshot.id", snapshot.id);
        console.log("snapshot.metadata", {
          fromCache: snapshot.metadata.fromCache,
          hasPendingWrites: snapshot.metadata.hasPendingWrites,
        });
        console.log("value", data);

        if (data) {
          const rawOrder = data as RawFirebaseOrder;
          const newOrder = normalizeFirebaseOrder(rawOrder, snapshot.id);

          console.log("rawKeys", Object.keys(rawOrder));
          console.log("rawMonetaryFields", {
            invoiceValue: rawOrder.invoiceValue ?? null,
            total: rawOrder.total ?? null,
            estimatedMinutes:
              rawOrder.estimated_minutes ?? rawOrder.estimatedMinutes ?? null,
            currencySymbol:
              rawOrder.currency_symbol ?? rawOrder.currencySymbol ?? null,
          });
          console.log("normalizedOrder", newOrder);
          console.log("normalizedItems", newOrder.items.map((item, index) => ({
            index,
            name: item.name,
            quantity: item.quantity,
            unitPrice: item.price,
            lineTotal: item.price * item.quantity,
          })));
          console.log("statusLabel", ORDER_STATUS_MAP[newOrder.status]);

          if (prevStatusRef.current !== null && prevStatusRef.current !== newOrder.status) {
            console.log("status change", {
              previous: prevStatusRef.current,
              previousLabel: ORDER_STATUS_MAP[prevStatusRef.current],
              next: newOrder.status,
              nextLabel: ORDER_STATUS_MAP[newOrder.status],
            });
            setStatusChanged(true);
          }

          prevStatusRef.current = newOrder.status;
          setOrder(newOrder);
          setError(null);
        } else {
          console.warn("[Firestore Track] No document found at path", sanitizedPath);
          setOrder(null);
          setError("Order not found");
        }

        console.groupEnd();
        setLoading(false);
      },
      (err) => {
        console.error("[Firestore Track] Listener error", {
          path: sanitizedPath,
          pathSegments,
          code: err.code,
          name: err.name,
          message: err.message,
        });
        setError(err.message);
        setLoading(false);
      }
    );

    return () => {
      console.log("[Firestore Track] Unsubscribe", sanitizedPath);
      unsubscribe();
    };
  }, [firebasePath]);

  const acknowledgeStatusChange = () => setStatusChanged(false);

  return { order, loading, error, statusChanged, acknowledgeStatusChange };
}
